import { Component, ReactNode } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, RefreshCw } from "lucide-react";
import * as Sentry from "@sentry/react";

interface State { error: Error | null; }

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State { return { error }; }

  componentDidCatch(error: Error, info: unknown) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info);
    try { Sentry.captureException(error, { extra: { info } }); } catch { /* noop */ }
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="max-w-lg w-full bg-panel border border-border p-8">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="w-6 h-6 text-primary" />
            <h1 className="text-2xl font-display tracking-widest">SOMETHING BROKE</h1>
          </div>
          <p className="text-muted-foreground font-body text-sm mb-6">
            The page hit an unexpected error and couldn't render. The team has been notified.
          </p>
          <pre className="text-xs font-mono bg-surface border border-border p-3 mb-6 overflow-auto max-h-48">
            {this.state.error.message}
          </pre>
          <div className="flex gap-2">
            <button
              onClick={() => { this.setState({ error: null }); location.reload(); }}
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-4 h-10 text-sm font-body font-semibold"
            >
              <RefreshCw className="w-4 h-4" /> Refresh page
            </button>
            <Link to="/" className="inline-flex items-center px-4 h-10 text-sm font-body border border-border">
              Go home
            </Link>
          </div>
        </div>
      </div>
    );
  }
}